import { Button as ButtonPrimitive } from '@base-ui/react/button';
import clsx from 'clsx';
import useTestTag from '../../common/hooks/use-test-tag';
import { mergeStyling } from '../common-functions';
import OdinIcon, { type OdinIconName, OdinIconSize } from '../odin-icon/odin-icon.component';

export type ButtonType = 'default' | 'primary' | 'text';
export type ButtonVariant = 'regular' | 'alternative';
export type ButtonHtmlType = 'submit' | 'reset' | 'button';

export interface ButtonProps extends Omit<ButtonPrimitive.Props, 'type'> {
  type?: ButtonType;
  variant?: ButtonVariant;
  classNames?: string;
  fullWidth?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  htmlType?: ButtonHtmlType;
}

interface IconButtonProps extends Omit<ButtonProps, 'type' | 'children' | 'classNames'> {
  type?: ButtonHtmlType;
  icon: OdinIconName;
  iconSize?: OdinIconSize;
  iconClassName?: string;
  spin?: boolean;
  showFocus?: boolean;
  /** Optional tooltip text; applied to title & aria-label for accessibility */
  tooltip?: string;
}

const commonButtonStyles =
  'inline-flex sm:w-auto text-md leading-5 rounded-sm focus:outline-hidden justify-center transition-colors duration-200 min-w-[3rem] min-h-[2.25rem] py-2 px-5 font-medium focus:ring-2 focus:ring-offset-2';

const buttonStylesMap: Record<ButtonType, Record<ButtonVariant, string>> = {
  primary: {
    regular: 'bg-primary-2 hover:bg-primary-3 text-mono-1 focus:ring-offset-mono-1 focus:ring-primary-2',
    alternative: 'bg-primary-1 hover:bg-primary-2 text-mono-1 focus:ring-primary-1',
  },
  default: {
    regular:
      'bg-mono-1 hover:bg-primary-5 text-primary-2 border border-solid border-primary-2 focus:ring-offset-mono-1 focus:ring-primary-2',
    alternative: 'hover:bg-primary-5 text-primary-1 border border-primary-1 focus:ring-primary-1',
  },
  text: {
    regular: 'text-primary-1 hover:text-primary-3 bg-transparent focus:ring-primary-3',
    alternative: 'text-primary-1 hover:text-primary-3 bg-transparent focus:ring-[0px] focus:ring-offset-0',
  },
};

const getButtonStyles = (type: ButtonType, variant: ButtonVariant) => {
  const styles = buttonStylesMap[type] || buttonStylesMap.default;
  return styles[variant];
};

export default function Button({
  type = 'default',
  variant = 'regular',
  classNames,
  className,
  fullWidth = true,
  hidden = false,
  disabled = false,
  htmlType = 'button',
  form,
  ...props
}: ButtonProps) {
  const buttonStyles = clsx(
    hidden ? 'hidden' : commonButtonStyles,
    { 'w-full': fullWidth },
    { 'opacity-50': disabled },
    getButtonStyles(type, variant),
    classNames,
    className
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      type={htmlType}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      {...props}
    />
  );
}

export function IconButton({
  onClick,
  className,
  type = 'button',
  form,
  disabled,
  icon,
  iconSize = OdinIconSize.Small,
  iconClassName,
  spin = false,
  showFocus = true,
  tooltip,
  ...rest
}: IconButtonProps) {
  const tagCreator = useTestTag('icon-button');
  const buttonStyles = mergeStyling({ 'cursor-not-allowed opacity-50': disabled }, className);

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      ref={tagCreator(icon.trim() ? icon.replace(/\s+/g, '-').toLowerCase() : '')}
      title={tooltip}
      aria-label={tooltip}
      {...rest}
    >
      <OdinIcon icon={icon} size={iconSize} className={iconClassName} spin={spin} showFocus={showFocus} />
    </button>
  );
}

export function Link({ classNames, className, type: _type, ...props }: ButtonProps) {
  const linkStyles = clsx(
    'p-0 text-primary-2 underline hover:no-underline text-md leading-5 m-1 bg-transparent focus:outline-primary-3 focus:outline-offset-4',
    classNames,
    className
  );

  return <button className={linkStyles} type="button" {...props} />;
}
